import {createAsyncThunk} from '@reduxjs/toolkit'

import {formatDateFns} from '@shared/helpers/date'
import {isEmpty} from '@shared/helpers/lang'
import {filterValueKeys} from '@shared/helpers/object'
import {qs} from '@shared/helpers/queryString'
import {SchemasOrderSaleSettle} from '@shared/schemas/order'

import {CATEGORY_DIVISION_KEY} from '@web/core/constants'
import {ROUTER_ORDER_SALE_SETTLEMENT} from '@web/core/routes'
import {asyncActionWrapper} from '@web/helpers/api'
import {push, replace} from '@web/slices/router'

import {fetchSettlementDetail, fetchSettlementHistory, fetchSettlementPayHold, fetchSettlementSummary} from './apis'
import {FetchSettlementDetail, FetchSettlementHistory, FetchSettlementSummary} from './apis/interfaces'

import type {ThunkApi} from './interfaces'

const actionType = 'saleSettlement'
const SaleSettlementUrl = ROUTER_ORDER_SALE_SETTLEMENT.MAIN.getPath()
const SaleSettlementSearchUrl = ROUTER_ORDER_SALE_SETTLEMENT.SEARCH.getPath()
const SaleSettlementPayHoldUrl = ROUTER_ORDER_SALE_SETTLEMENT.PAY_HOLD.getPath()

/** 정산내역 조회 */
export const getSettlementHistory = createAsyncThunk<
    FetchSettlementHistory['response'],
    FetchSettlementHistory['request'],
    ThunkApi
>('getSettlementHistory', async ({startDate, endDate, keyword, category, detail, page}, {signal, rejectWithValue}) => {
    return asyncActionWrapper(
        () => fetchSettlementHistory({signal, startDate, endDate, keyword, category, detail, page}),
        {
            rejectWithValue,
        },
    )
})

/** 정산내역 > 정산요약 조회 */
export const getSettlementSummary = createAsyncThunk<
    FetchSettlementSummary['response'],
    FetchSettlementSummary['request'],
    ThunkApi
>('getSettlementSummary', async ({startDate, endDate}, {rejectWithValue}) => {
    return asyncActionWrapper(() => fetchSettlementSummary({startDate, endDate}), {
        rejectWithValue,
    })
})

/** 정산상세 조회 */
export const getSettlementDetail = createAsyncThunk<
    FetchSettlementDetail['response'],
    FetchSettlementDetail['request'],
    ThunkApi
>('getSettlementDetail', async ({settleObjectNo}, {rejectWithValue}) => {
    return asyncActionWrapper(() => fetchSettlementDetail({settleObjectNo}), {rejectWithValue})
})

/** 지급 보류 상세 내역 조회 */
export const getSettlementPayHold = createAsyncThunk<
    SchemasOrderSaleSettle['/pay/hold']['response'],
    SchemasOrderSaleSettle['/pay/hold']['request'],
    ThunkApi
>('getSettlementPayHold', async ({startDate, endDate}, {rejectWithValue}) => {
    return asyncActionWrapper(() => fetchSettlementPayHold({startDate, endDate}), {rejectWithValue})
})

const getUrl = ({
    url,
    page,
    keyword,
    categories,
    details,
    startDate,
    endDate,
    search,
}: SearchParams & {
    url: string
    page?: number
}) => {
    return qs.stringifyUrlBySort<{
        page?: Maybe<string>
        keyword?: Maybe<string>
        category?: Maybe<string>
        detail?: Maybe<string>
        startDate?: Maybe<string>
        search?: Maybe<string>
        endDate?: Maybe<string>
    }>({
        url,
        query: filterValueKeys((v) => !!v, {
            page: page && page > 0 ? `${page}` : null,
            keyword: keyword ? `${keyword}`.trim() : null,
            category: categories ? categories.join(CATEGORY_DIVISION_KEY) : null,
            detail: details ? details.join(CATEGORY_DIVISION_KEY) : null,
            startDate: startDate ? formatDateFns(new Date(startDate), 'yyyy-MM-dd') : null,
            endDate: endDate ? formatDateFns(new Date(endDate), 'yyyy-MM-dd') : null,
            search: [keyword, categories, startDate, endDate].some(isEmpty) ? search : null,
        }),
        sorts: ['search', 'startDate', 'endDate', 'page', 'category', 'detail', 'keyword'],
    })
}

/** 검색창 검색 */
export const searchSaleSettlement = createAsyncThunk<
    void,
    SearchParams & {
        page?: number
        type?: 'PUSH' | 'REPLACE'
    },
    ThunkApi
>(`${actionType}/search`, ({type, page, keyword, categories, details, startDate, endDate, search}, {dispatch}) => {
    const url = getUrl({
        url: `${SaleSettlementUrl}`,
        page,
        keyword,
        categories,
        details,
        startDate,
        endDate,
        search,
    })

    dispatch(
        type === 'REPLACE'
            ? replace(url, {
                  resetScroll: true,
              })
            : push(url, {
                  resetScroll: true,
              }),
    )
})

export const goSearchSaleSettlement = createAsyncThunk<void, SearchParams, ThunkApi>(
    `${actionType}/search`,
    ({keyword, categories, startDate, endDate}, {dispatch}) => {
        dispatch(
            push(
                getUrl({
                    url: `${SaleSettlementSearchUrl}`,
                    keyword,
                    categories,
                    startDate,
                    endDate,
                }),
            ),
        )
    },
)

export const goPayHoldSaleSettlement = createAsyncThunk<void, SearchParams, ThunkApi>(
    `${actionType}/search`,
    ({startDate, endDate}, {dispatch}) => {
        dispatch(
            push(
                getUrl({
                    url: `${SaleSettlementPayHoldUrl}`,
                    startDate,
                    endDate,
                }),
            ),
        )
    },
)

type SearchParams = {
    keyword?: string
    categories?: string[]
    details?: string[]
    startDate?: string
    endDate?: string
    search?: 'filter' | 'advanced'
}
